import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import React, { ReactElement, useMemo } from 'react';

import { Account } from '~hooks/useAccount/models';
import { useJob } from '~hooks/useJob/useJob';
import { User } from '~hooks/useUsers/models';

import { OverflowAwareText } from '../../Order/ordersDispatchStyledComponents';

interface AssignedViewProp {
  customUnassignCTA?: ReactElement;
  driver?: User;
  editable?: boolean;
  isUnassignable?: boolean;
  valueToDisplay?: 'driver' | 'vendor' | 'collaborator';
  vendor?: Account;
  onUnassignClick: () => void;
}

const AssignedDriverView = ({
  customUnassignCTA,
  driver,
  editable,
  isUnassignable,
  valueToDisplay = 'vendor',
  vendor,
  onUnassignClick,
}: AssignedViewProp) => {
  const theme = useTheme();
  const { isUpdating } = useJob();
  const displayName = useMemo(() => {
    if (driver && valueToDisplay === 'driver') {
      return `${driver.firstName} ${driver.lastName}`;
    } else if (vendor && valueToDisplay === 'vendor') {
      return vendor?.name || '';
    }
    return '';
  }, [driver, vendor, valueToDisplay]);

  if (editable && isUnassignable && customUnassignCTA) {
    return (
      <Tooltip title={t('dispatch.job.assignment_popover.unassign')}>
        {React.cloneElement(customUnassignCTA, {
          onClick(event: React.MouseEvent) {
            event?.stopPropagation();
            onUnassignClick();
          },
        })}
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
        <Tooltip
          title={displayName}
          {...(valueToDisplay === 'driver' ? { 'data-sentry-mask': true } : {})}
        >
          <OverflowAwareText>{displayName}</OverflowAwareText>
        </Tooltip>

        {isUnassignable ? (
          <Tooltip title={t('dispatch.job.assignment_popover.unassign')}>
            <IconButton
              size="small"
              onClick={onUnassignClick}
              sx={{ color: theme.palette.text.secondary }}
              disabled={isUpdating}
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
};

export { AssignedDriverView };
